<template>
  <div class="image-upload">
    <div @click="inputElement.click()">
      <slot />
    </div>
    <input
      ref="inputElement"
      class="upload-input"
      data-external="document-edit-recipient-modal-upload-image-input"
      type="file"
      :multiple="multiple"
      :accept="acceptedFileTypes"
      @change="handleFileInput"
    />
  </div>
</template>

<script lang="ts">
import { allAcceptedFileTypes } from '@getaccept/lib-shared/src/file/constants/accepted-file-types';
import { defineComponent, ref } from 'vue';

export default defineComponent({
  inheritAttrs: false,
  props: {
    acceptedFileTypes: { type: String, default: allAcceptedFileTypes.toString() },
    multiple: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },
  emits: ['selectFile', 'invalidFile'],
  setup(props, { emit }) {
    const inputFile = ref<File | null>(null);
    const validFile = ref<boolean>(false);
    const inputValue = ref('');
    const inputElement = ref<HTMLInputElement | null>(null);

    const handleMultipleFiles = (files: FileList) => {
      const validFiles = [];
      const invalidFiles = [];

      for (let index = 0; index < files.length; index++) {
        const validFile = validateFileType(files[index]);
        if (!validFile) {
          invalidFiles.push(files[index]);
        } else {
          validFiles.push(files[index]);
        }
      }
      if (invalidFiles.length) {
        emit('invalidFile', invalidFiles);
      }
      if (validFiles.length) {
        emit('selectFile', validFiles);
      }
    };

    const handleFileInput = (event: Event) => {
      if (props.disabled) {
        return;
      }
      const { files } = event.target as HTMLInputElement;
      if (!files) {
        return;
      }

      if (props.multiple) {
        handleMultipleFiles(files);
        return;
      }

      inputFile.value = files[0];
      validFile.value = validateFileType(inputFile.value);

      if (!inputFile.value || !validFile.value) {
        emit('invalidFile');
        return;
      }
      emit('selectFile', inputFile.value);
      inputValue.value = '';
    };

    const validateFileType = (inputFile: File) => {
      const fileName: string = inputFile?.name || '';
      const dotIndex: number = fileName.lastIndexOf('.');
      if (dotIndex < 0) {
        return false;
      }
      const fileType: string = fileName.substr(dotIndex).toLowerCase();

      return props.acceptedFileTypes ? props.acceptedFileTypes.includes(fileType) : true;
    };

    return {
      inputFile,
      validFile,
      handleFileInput,
      validateFileType,
      inputElement,
    };
  },
});
</script>

<style lang="scss" scoped>
.upload-input {
  visibility: hidden;
  position: absolute;
  width: 0;
}
</style>
